import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link, useNavigate } from 'react-router-dom';
import { ALLURE_ALIASES, type ZoneRow } from '../Construction/allureTable.ts';
import { calculerSyntheseSeance, CONDITIONS, formatTemps } from '../Construction/seanceEngine.ts';
import { formaterDuree } from '../Construction/formatDuree.ts';
import { useVma } from '../Allures/useVma.ts';
import { useAllureTable } from '../Allures/useAllureTable.ts';
import { COULEURS_ZONE, COULEUR_INCONNUE } from '../Allures/couleursZones.tsx';
import { ERREURS, EXEMPLES, EXEMPLES_CLUB, type Exemple } from './guideNotation.ts';

// Guide de la notation des seances, pour les coachs : chaque regle est
// illustree d'exemples calcules en direct par le moteur (seanceEngine.ts)
// avec la VMA et les parametres d'allures du coach, pour que la duree et
// la distance affichees soient celles de l'ecran de saisie. Les exemples
// sont dans guideNotation.ts (verifies par guideNotation.test.ts).

export const CHEMIN_GUIDE_NOTATION = '/guide-notation';

const Calcul = React.createContext<{ vma: number; table: ZoneRow[] }>({ vma: 16, table: [] });

const Code = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="code"
    sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.5, py: 0.1, borderRadius: 0.5, wordBreak: 'break-word' }}
  >
    {children}
  </Box>
);

/** Duree et distance estimees d'une notation (ou l'erreur du moteur). */
function useResume(notation: string): { texte: string; erreur?: string } {
  const { vma, table } = React.useContext(Calcul);
  const s = calculerSyntheseSeance(notation, vma, table);
  if (s.erreur) return { texte: '', erreur: s.erreur };
  return { texte: `${formaterDuree(s.dureeTotaleS)} · ${(s.distanceTotaleM / 1000).toFixed(1).replace('.', ',')} km · RPE ${s.rpeSeance}` };
}

const LigneExemple = ({ exemple }: { exemple: Exemple }) => {
  const resume = useResume(exemple.notation);
  return (
    <TableRow>
      <TableCell sx={{ fontFamily: 'monospace', whiteSpace: { md: 'nowrap' }, wordBreak: 'break-word' }}>{exemple.notation}</TableCell>
      <TableCell>{exemple.lecture}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', color: resume.erreur ? 'error.main' : 'text.secondary' }}>
        {resume.erreur ?? resume.texte}
      </TableCell>
    </TableRow>
  );
};

const Exemples = ({ exemples }: { exemples: Exemple[] }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Notation</TableCell>
          <TableCell>Lecture</TableCell>
          <TableCell>Estimation</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {exemples.map((e) => <LigneExemple key={e.notation} exemple={e} />)}
      </TableBody>
    </Table>
  </TableContainer>
);

const Section = ({ titre, children }: { titre: string; children: React.ReactNode }) => (
  <Box component="section" sx={{ mt: 4 }}>
    <Typography variant="h6" component="h2" gutterBottom>{titre}</Typography>
    {children}
  </Box>
);

/** Decomposition visuelle de 6x(800m@5k-R2'). */
const Anatomie = () => {
  const parties = [
    { texte: '6x', role: 'répétitions' },
    { texte: '(', role: '' },
    { texte: '800m', role: 'effort' },
    { texte: '@5k', role: 'allure' },
    { texte: "-R2'", role: 'récupération' },
    { texte: ')', role: '' },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'flex-start' }}>
      {parties.map((p, i) => (
        <Box key={i} sx={{ textAlign: 'center' }}>
          <Box sx={{ fontFamily: 'monospace', fontSize: '1.6rem', px: 0.5, borderBottom: p.role ? 2 : 0, borderColor: 'primary.main' }}>
            {p.texte}
          </Box>
          <Typography variant="caption" color="text.secondary">{p.role}</Typography>
        </Box>
      ))}
    </Paper>
  );
};

/** Allures reconnues, avec les % VMA du coach et l'allure correspondante a sa VMA. */
const TableAllures = () => {
  const { vma, table } = React.useContext(Calcul);
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Aussi accepté</TableCell>
            <TableCell>Zone</TableCell>
            <TableCell align="right">% VMA</TableCell>
            <TableCell align="right">Allure à {String(vma).replace('.', ',')} km/h</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {table.map((z) => (
            <TableRow key={z.code}>
              <TableCell>
                <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', mr: 1, bgcolor: COULEURS_ZONE[z.code] ?? COULEUR_INCONNUE }} />
                <Code>{z.code}</Code>
              </TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>
                {(ALLURE_ALIASES[z.code] ?? []).filter((a) => a !== z.code).join(', ') || '—'}
              </TableCell>
              <TableCell>{z.nom}</TableCell>
              <TableCell align="right">{Math.round(z.pctVMA * 100)} %</TableCell>
              <TableCell align="right">{formatTemps(3600 / (vma * z.pctVMA))} /km</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/** Zone de test : estimation en direct, et creation de la seance. */
const Essai = () => {
  const navigate = useNavigate();
  const [notation, setNotation] = React.useState("3x(8'@MP-R1'30''-3'@10k-R3')");
  const resume = useResume(notation);
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <TextField
          label="Notation à tester"
          value={notation}
          onChange={(e) => setNotation(e.target.value)}
          sx={{ flex: 1, minWidth: { xs: '100%', sm: 360 } }}
          inputProps={{ style: { fontFamily: 'monospace' } }}
        />
        <Button
          variant="outlined"
          sx={{ mt: 1 }}
          disabled={!notation.trim() || !!resume.erreur}
          onClick={() => navigate('/seances/nouvelle', { state: { notation: notation.trim() } })}
        >
          Créer cette séance
        </Button>
      </Box>
      {notation.trim() && (
        resume.erreur
          ? <Alert severity="warning" sx={{ mt: 1 }}>{resume.erreur}</Alert>
          : <Alert severity="success" sx={{ mt: 1 }}>Notation reconnue : {resume.texte}</Alert>
      )}
    </Paper>
  );
};

const GuideNotation = () => {
  const [vma] = useVma({ exigee: false });
  const { table } = useAllureTable();
  const calcul = React.useMemo(() => ({ vma, table }), [vma, table]);
  const e = EXEMPLES;

  return (
    <Calcul.Provider value={calcul}>
      <Box component="main" sx={{ p: { xs: 2, md: 12 }, pt: { xs: 10, md: 10 }, maxWidth: 1100, mx: 'auto' }}>
        <Typography variant="h4" component="h1" gutterBottom>Guide de la notation des séances</Typography>
        <Typography paragraph>
          Une séance s'écrit sur une seule ligne. Chaque effort dit <strong>combien</strong> (une durée ou une
          distance), <strong>à quelle allure</strong> (après <Code>@</Code>) et, s'il est répété,{' '}
          <strong>quelle récupération</strong> (après <Code>-R</Code>). L'application en déduit la durée, la
          distance, l'intensité (RPE) et la séance envoyée sur la montre.
        </Typography>
        <Typography paragraph variant="body2" color="text.secondary">
          Les estimations de cette page sont calculées avec votre VMA ({String(vma).replace('.', ',')} km/h) et
          vos <Link to="/preferences?onglet=allures">paramètres d'allures</Link> ; pour un athlète, ce sont les
          siens qui s'appliquent.
        </Typography>

        <Anatomie />

        <Section titre="1. L'effort : une durée ou une distance">
          <Typography paragraph>
            Durées : <Code>8'</Code> ou <Code>8min</Code> pour les minutes, <Code>45''</Code>, <Code>45"</Code> ou{' '}
            <Code>45s</Code> pour les secondes, <Code>1'15''</Code> pour 1 min 15. Les heures ne sont pas
            reconnues : écrire <Code>90'</Code>. Distances : <Code>400m</Code>, <Code>1km</Code> ou{' '}
            <Code>1k</Code> ; la virgule est acceptée (<Code>5,5km</Code>). Avant le <Code>@</Code>, « k » veut
            toujours dire kilomètres.
          </Typography>
          <Exemples exemples={[e.duree, e.dureeSecondes, e.distance, e.distanceMetres, e.distanceK]} />
        </Section>

        <Section titre="2. L'allure : après @">
          <Typography paragraph>
            L'allure est un code de zone, en majuscules ou minuscules. Elle donne la vitesse de l'effort
            (pour estimer la distance d'un effort en temps, et inversement) et la cible envoyée à la montre,
            une plage entre l'allure indiquée et la zone juste plus lente.
          </Typography>
          <TableAllures />
          <Typography paragraph>
            <strong>Effort progressif</strong> : deux codes séparés par un tiret, de l'allure de départ à
            l'allure d'arrivée. Le calcul se fait à l'allure moyenne des deux.
          </Typography>
          <Exemples exemples={[e.progressif]} />
        </Section>

        <Section titre="3. La récupération : -R">
          <Typography paragraph>
            La récupération se place après l'allure : <Code>-R2'</Code>, <Code>-R1'30''</Code>,{' '}
            <Code>-R90"</Code>, <Code>-R0'30''</Code>. Un nombre seul compte en minutes (<Code>-R2</Code> = 2').
            Elle peut aussi être une distance, en <Code>m</Code> ou <Code>km</Code> (<Code>-R400m</Code>,{' '}
            <Code>-R1km</Code>, mais pas <Code>-R1k</Code>) : elle est alors convertie en temps à l'allure EASY.
          </Typography>
          <Typography paragraph>
            Elle se court <strong>entre</strong> deux répétitions : <Code>4x(8'@HP-R2')</Code> compte 3
            récupérations, pas 4. Si un autre bloc suit (voir <Code>+</Code> plus bas), la récupération est
            aussi courue après la dernière répétition, avant ce bloc. Sans <Code>-R</Code>, les répétitions s'enchaînent sans pause. L'ajustement
            des récupérations choisi dans les préférences (ex : +15'') s'applique aux récupérations en temps.
          </Typography>
          <Exemples exemples={[e.recupTemps, e.recupSecondes, e.recupDistance, e.sansRecup]} />
        </Section>

        <Section titre="4. Les répétitions : Nx(...)">
          <Typography paragraph>
            <Code>Nx</Code> répète ce qui suit, et ce qui est répété <strong>doit être entre
            parenthèses</strong> : <Code>6x(1000m@10K-R2')</Code>, jamais <Code>6x1000m@10K-R2'</Code>.
            Le « x » peut s'écrire <Code>x</Code>, <Code>X</Code> ou <Code>×</Code>.
          </Typography>
          <Exemples exemples={[e.anatomie, e.repetitions]} />
        </Section>

        <Section titre="5. Enchaîner des blocs : +">
          <Typography paragraph>
            <Code>+</Code> met des blocs bout à bout. La récupération écrite dans un bloc sert aussi de
            transition vers le bloc suivant ; un bloc sans récupération enchaîne directement sur le suivant.
            Pour une pause différente, ajouter un bloc de footing lent, par exemple <Code>+3'@RECUP+</Code>.
            C'est aussi la façon d'écrire une pyramide ou une séance complète (échauffement, corps, retour au
            calme). La dernière récupération de la séance n'est pas comptée dans la durée.
          </Typography>
          <Exemples exemples={[e.enchainement, e.enchainementSansRecup, e.enchainementRecup, e.pyramide, e.seanceComplete]} />
        </Section>

        <Section titre="6. Plusieurs efforts par répétition">
          <Typography paragraph>
            Dans les parenthèses, plusieurs efforts s'enchaînent avec <Code>-</Code> (ou <Code>+</Code>, qui
            a alors le même sens). Chaque effort peut avoir sa récupération ; la dernière récupération
            sépare deux tours (et précède le bloc suivant s'il y en a un). Sans <Code>Nx</Code>, la suite est
            courue une seule fois.
          </Typography>
          <Exemples exemples={[e.compose, e.composePlus, e.alternance, e.sequenceSansRepetition]} />
        </Section>

        <Section titre="7. Des séries de répétitions">
          <Typography paragraph>
            Trois écritures, qui ne comptent pas la récupération entre les séries de la même façon :
          </Typography>
          <Box component="ul" sx={{ mt: 0 }}>
            <li><Code>2x5x(...)</Code> : simple raccourci de <Code>10x(...)</Code>, même récupération partout ;</li>
            <li><Code>2x(5x(...)-R3')</Code> : une récupération propre entre les séries ;</li>
            <li><Code>5x(...)+2'@RECUP+5x(...)</Code> : les séries écrites une à une, séparées par un footing lent.</li>
          </Box>
          <Exemples exemples={[e.seriesAplaties, e.seriesImbriquees, e.seriesPlus]} />
        </Section>

        <Section titre="8. Le terrain : [côte], [piste]...">
          <Typography paragraph>
            Une étiquette entre crochets, <strong>collée juste après l'allure</strong>, précise où se court
            l'effort : {CONDITIONS.map((c, i) => (
              <React.Fragment key={c.code}>{i > 0 && ', '}<Code>[{c.libelle.toLowerCase()}]</Code></React.Fragment>
            ))}. Les accents et majuscules sont libres (<Code>[cote]</Code>, <Code>[montée]</Code>,{' '}
            <Code>[stade]</Code>, <Code>[chemin]</Code> aussi). L'étiquette ne change pas les calculs. En côte,
            descente et trail, l'allure n'est qu'un repère d'intensité : la montre n'affiche pas de cible
            d'allure.
          </Typography>
          <Exemples exemples={[e.condition, e.conditionPiste]} />
        </Section>

        <Section titre="Erreurs fréquentes">
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Refusé</TableCell>
                  <TableCell>Pourquoi</TableCell>
                  <TableCell>Écrire plutôt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ERREURS.map((x) => (
                  <TableRow key={x.notation}>
                    <TableCell sx={{ fontFamily: 'monospace', color: 'error.main' }}>{x.notation}</TableCell>
                    <TableCell>{x.probleme}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{x.correction}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>

        <Section titre="Séances du club">
          <Exemples exemples={EXEMPLES_CLUB} />
        </Section>

        <Section titre="Essayer une notation">
          <Essai />
        </Section>
      </Box>
    </Calcul.Provider>
  );
};

export default GuideNotation;
